"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Circle } from "@/components/icons";
import { Button } from "@/components/ui/button";
import EventLink from "@/components/Link/EventLink/EventLink";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import TestimonialCard, { TestimonialCardData } from "./TestimonialCard";
import {
  getEmblaSlideAccessibilityProps,
  isEmblaSlideInView,
} from "@/components/Carousel/emblaSlideAccessibility";
import { useEmblaSlidesInView } from "@/components/Carousel/useEmblaSlidesInView";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";
import "./styles.css";

// Color mappings for different background themes
const backgroundColors = {
  elm: "bg-primary-500",
  putty: "bg-putty-100",
  "putty-200": "bg-putty-200",
  "putty-300": "bg-putty-300",
  fern: "bg-secondary-500",
  white: "bg-white",
};

const textColors = {
  elm: "text-white",
  fern: "text-white",
  putty: "",
  "putty-200": "",
  "putty-300": "",
  white: "",
};

const cardBackgroundColors = {
  elm: "bg-white",
  putty: "bg-white",
  "putty-200": "bg-white",
  "putty-300": "bg-white",
  fern: "bg-white",
  white: "bg-putty-100",
};

const controlBackgroundColors = {
  elm: "bg-white",
  putty: "bg-white",
  "putty-200": "bg-white",
  "putty-300": "bg-white",
  fern: "bg-white",
  white: "bg-putty-100",
};

const defaultEventConfig = {
  name: "social-proof-link",
  htmlId: "social-proof-link",
  location: "homepage",
  elementType: "social-proof-link",
};

export type SocialProofBackground =
  | "elm"
  | "putty"
  | "putty-200"
  | "putty-300"
  | "fern"
  | "white";

interface EventConfig {
  name: string;
  htmlId: string;
  location: string;
  elementType: string;
}

interface SocialProofProps {
  useAutoplay?: boolean;
  autoplayDelay?: number;
  header: string;
  headerTextClassname?: string;
  items: TestimonialCardData[];
  linkText: string;
  linkUrl: string;
  background?: SocialProofBackground;
  location?: string;
  containerClassName?: string;
  isSideRailShown?: boolean;
  eventConfig?: EventConfig;
}

const getEmblaOptions = (isSideRailShown?: boolean): EmblaOptionsType => {
  return {
    loop: false,
    align: "start",
    containScroll: false,
    slidesToScroll: 1, // Mobile: scroll 1 slide
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 2 }, // Medium: scroll 2 slides
      "(min-width: 1024px)": { slidesToScroll: isSideRailShown ? 2 : 3 }, // Large: scroll 2 or 3 slides based on side rail
    },
  };
};

// Carousel hooks (copied from homepage hero implementation)
type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

const useDotButton = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (
    emblaApi: EmblaCarouselType,
    direction: "prev" | "next" | "dot",
  ) => void,
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      if (onButtonClick) onButtonClick(emblaApi, "dot");
    },
    [emblaApi, onButtonClick],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (
    emblaApi: EmblaCarouselType,
    direction: "prev" | "next",
  ) => void,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    if (onButtonClick) onButtonClick(emblaApi, "prev");
  }, [emblaApi, onButtonClick]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    if (onButtonClick) onButtonClick(emblaApi, "next");
  }, [emblaApi, onButtonClick]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

export default function SocialProof({
  useAutoplay = true,
  autoplayDelay = 5000,
  header,
  headerTextClassname = "elm-h2 text-center mb-10",
  items,
  linkText,
  linkUrl,
  background = "putty",
  location = "homepage",
  containerClassName = "py-20 px-4 md:px-10 mx-break-out lg:px-[120px]",
  isSideRailShown = false,
  eventConfig = defaultEventConfig,
}: SocialProofProps) {
  const carouselMessages = useCarouselMessages();

  const plugins = useAutoplay ? [Autoplay({ delay: autoplayDelay })] : [];
  const options = getEmblaOptions(isSideRailShown);
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  const { elementViewed } = useElementEvents({
    webElement: {
      name: "testimonials-module",
      elementType: "testimonials-module",
      location: location,
      htmlId: "testimonials-module",
      text: "Testimonials Module",
    },
  });

  const { elementClicked: prevNavButtonClicked } = useElementEvents({
    webElement: {
      name: "testimonials-module",
      elementType: "testimonials-module",
      location: location,
      htmlId: "testimonials-module",
      text: "Previous",
    },
  });

  const { elementClicked: nextNavButtonClicked } = useElementEvents({
    webElement: {
      name: "testimonials-module",
      elementType: "testimonials-module",
      location: location,
      htmlId: "testimonials-module",
      text: "Next",
    },
  });

  const { elementClicked: dotNavButtonClicked } = useElementEvents({
    webElement: {
      name: "testimonials-module",
      elementType: "testimonials-module",
      location: location,
      htmlId: "testimonials-module",
      text: "Dot",
    },
  });

  const onNavButtonClick = useCallback(
    (emblaApi: EmblaCarouselType, direction: "prev" | "next" | "dot") => {
      const autoplay = emblaApi?.plugins()?.autoplay;
      if (!autoplay) return;
      autoplay.reset();
      switch (direction) {
        case "prev":
          prevNavButtonClicked();
          break;
        case "next":
          nextNavButtonClicked();
          break;
        case "dot":
          dotNavButtonClicked();
      }
    },
    [prevNavButtonClicked, nextNavButtonClicked, dotNavButtonClicked],
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  const slidesInView = useEmblaSlidesInView(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onNavButtonClick);

  const { ref: socialProofRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const hasTriggeredViewEvent = useRef(false);

  useEffect(() => {
    if (inView && !hasTriggeredViewEvent.current) {
      elementViewed();
      hasTriggeredViewEvent.current = true;
    }
  }, [inView, elementViewed]);

  // Dynamic color classes based on background prop
  const backgroundColorClass =
    backgroundColors[background] || backgroundColors.putty;
  const textColor = textColors[background] || textColors.putty;
  const cardBackgroundColor =
    cardBackgroundColors[background] || cardBackgroundColors.putty;
  const controlBackgroundColor =
    controlBackgroundColors[background] || controlBackgroundColors.putty;

  const [currentSlidesToScroll, setCurrentSlidesToScroll] = useState(1);

  useEffect(() => {
    const updateSlidesToScroll = () => {
      if (window.innerWidth >= 1024) {
        setCurrentSlidesToScroll(isSideRailShown ? 2 : 3);
      } else if (window.innerWidth >= 768) {
        setCurrentSlidesToScroll(2);
      } else {
        setCurrentSlidesToScroll(1);
      }
    };

    updateSlidesToScroll();
    window.addEventListener("resize", updateSlidesToScroll);
    return () => window.removeEventListener("resize", updateSlidesToScroll);
  }, [isSideRailShown]);

  const shouldShowControls = items.length > currentSlidesToScroll;

  const dataItemsCount = Math.min(items.length, 3);

  return (
    <div
      ref={socialProofRef}
      data-items={dataItemsCount}
      className={`embla social-proof ${backgroundColorClass} flex flex-col items-center ${containerClassName} ${isSideRailShown ? "side-rail-shown" : ""}`}
    >
      {header && (
        <h2 className={`${headerTextClassname} ${textColor}`}>{header}</h2>
      )}

      {/* Slides */}
      <div
        id="carousel-container"
        className="embla__viewport w-full md:w-screen lg:w-full lg:max-w-[1200px] mx-auto px-2"
        ref={emblaRef}
        role="region"
        aria-label={carouselMessages.socialProofCarousel}
        aria-roledescription="carousel"
        style={{
          overflow: "hidden",
        }}
      >
        <div className="embla__container flex">
          {items.map((slide, index) => {
            const isVisible = isEmblaSlideInView(index, slidesInView);

            return (
              <div
                key={index}
                aria-label={carouselMessages.slideLabel(index + 1)}
                aria-roledescription="slide"
                {...getEmblaSlideAccessibilityProps(isVisible)}
                className="embla__slide md:w-[100px] lg:w-[160px]"
              >
                <TestimonialCard
                  content={slide.content}
                  image={slide.image}
                  name={slide.name}
                  productName={slide.productName}
                  cardBackgroundColor={cardBackgroundColor}
                  itemsCount={items.length}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Controls - Only show if there are enough slides */}
      {shouldShowControls && (
        <div className="w-full px-8 mt-10 flex flex-row justify-center">
          <div
            className={`flex flex-row justify-center w-fit items-center gap-2 ${controlBackgroundColor} rounded-full`}
          >
            {/* Previous Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              aria-label={carouselMessages.previousSlide}
              className="hover:bg-transparent"
            >
              <ChevronLeft />
            </Button>

            {/* Dots */}
            <div className="flex gap-6">
              {scrollSnaps.map((_, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="icon"
                  onClick={() => onDotButtonClick(index)}
                  aria-label={carouselMessages.goToSlide(index + 1)}
                  className={`h-3 w-3 rounded-full p-0 ${
                    index === selectedIndex ? "bg-brand-500" : "bg-primary-500"
                  }`}
                >
                  <Circle
                    svgProps={{
                      width: "8",
                      height: "8",
                      className: `${index === selectedIndex ? "text-brand-500" : "text-primary-500"}`,
                    }}
                  />
                </Button>
              ))}
            </div>

            {/* Next Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              aria-label={carouselMessages.nextSlide}
              className="hover:bg-transparent"
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}

      {linkText && linkUrl && (
        <EventLink
          href={linkUrl}
          text={linkText}
          html_id={eventConfig.htmlId}
          element_type={eventConfig.elementType}
          location={eventConfig.location}
          name={eventConfig.name}
          class_name={`text-center mt-10 underline hover:text-primary-500 hover:no-underline hover:font-bold underline-offset-4 text-base ${textColor === "text-white" ? "text-white" : "text-primary-500"}`}
        />
      )}
    </div>
  );
}
